import { useState, useEffect } from 'react';
import { Save, ArrowRightLeft } from 'lucide-react';
import Modal from '../../common/Modal';
import edgeService from '../../../services/edge.service';
import { isValidIPv4 } from '../../../utils/ip';

interface PortForwardingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  subscription?: any;
  mode: 'add' | 'edit';
  edgeId: string;
  /**
   * Pre-locked WAN interface — used when the modal is embedded inside the
   * parent Logical Interface modal. Hides the interface selector.
   */
  lockedInterfaceId?: string;
  /**
   * Draft mode: the parent WAN interface doesn't exist yet — it's composed with
   * this forward for a single atomic commit. Supplies the interface name +
   * aliases so the modal doesn't depend on the server list; logical_port_id is
   * left empty for the commit endpoint to assign.
   */
  draftInterface?: { name: string; role?: string; ipv4?: string; ipv4Aliases?: any[] };
}

const isValidPort = (port: string): boolean => {
  const n = parseInt(port, 10);
  return !isNaN(n) && n >= 1 && n <= 65535 && String(n) === port.trim();
};

const PortForwardingModal = ({ isOpen, onClose, onSave, subscription, mode, edgeId, lockedInterfaceId, draftInterface }: PortForwardingModalProps) => {
  const isDraft = !!draftInterface && !lockedInterfaceId;
  const [logicalInterfaces, setLogicalInterfaces] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logicalInterface: '',
    /**
     * VSNP-691: when the parent WAN has alias IPs (`ipv4Aliases`), the
     * operator can pin this forward to a specific external IP. Empty
     * string = "use the parent's primary" (legacy default).
     */
    externalIp: '',
    protocol: 'TCP',
    externalPort: '',
    internalIp: '',
    internalPort: '',
    enabled: true,
  });

  // Populate form from existing subscription on edit
  useEffect(() => {
    if (isOpen) {
      const config = subscription?.configuration;
      setFormData({
        name: subscription?.subscription_name || subscription?.name || '',
        description: subscription?.description || '',
        logicalInterface: subscription?.logical_port_id || config?.logical_port_id || lockedInterfaceId || '',
        externalIp: config?.external_ip || '',
        protocol: config?.protocol || 'TCP',
        externalPort: config?.external_port?.toString() || '',
        internalIp: config?.internal_ip || '',
        internalPort: config?.internal_port?.toString() || '',
        enabled: config?.enabled ?? true,
      });
      setErrors({});
    }
  }, [isOpen, subscription]);

  // Fetch real logical interfaces
  useEffect(() => {
    const fetchInterfaces = async () => {
      if (!isOpen || !edgeId) return;
      try {
        setLoading(true);
        const response = await edgeService.listLogicalInterfaces(edgeId);
        const interfaces = response?.data?.items || response?.items || response || [];
        setLogicalInterfaces(interfaces);
      } catch (err: any) {
        console.error('Failed to fetch logical interfaces:', err);
        setLogicalInterfaces([]);
      } finally {
        setLoading(false);
      }
    };
    fetchInterfaces();
  }, [isOpen, edgeId]);

  const wanInterfaces = logicalInterfaces.filter((iface: any) =>
    iface.role === 'WAN' || iface.name?.toLowerCase().includes('wan')
  );

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less';
    }
    if (!formData.logicalInterface && !isDraft) newErrors.logicalInterface = 'WAN interface is required';
    if (!formData.externalPort.trim()) {
      newErrors.externalPort = 'External port is required';
    } else if (!isValidPort(formData.externalPort)) {
      newErrors.externalPort = 'Port must be 1-65535';
    }
    if (!formData.internalIp.trim()) {
      newErrors.internalIp = 'Internal IP is required';
    } else if (!isValidIPv4(formData.internalIp)) {
      newErrors.internalIp = 'Must be a valid IPv4 address';
    }
    if (!formData.internalPort.trim()) {
      newErrors.internalPort = 'Internal port is required';
    } else if (!isValidPort(formData.internalPort)) {
      newErrors.internalPort = 'Port must be 1-65535';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const selectedInterface = logicalInterfaces.find((i: any) => i.id === formData.logicalInterface);
    const interfaceName = isDraft ? draftInterface!.name : (selectedInterface?.name || '');

    const apiData = {
      subscription_type: 'port_forwarding',
      subscription_name: formData.name,
      description: formData.description,
      logical_port_id: isDraft ? '' : formData.logicalInterface,
      configuration: {
        external_port: parseInt(formData.externalPort),
        // VSNP-691: empty external_ip = use the parent's primary IP
        // (the API/agent fall back to that automatically). Only persist
        // an explicit value when the operator picks an alias.
        ...(formData.externalIp ? { external_ip: formData.externalIp } : {}),
        internal_ip: formData.internalIp,
        internal_port: parseInt(formData.internalPort),
        protocol: formData.protocol,
        wan_interface: interfaceName,
        enabled: formData.enabled,
      },
    };

    onSave(apiData);
    onClose();
  };

  const selectedInterface = logicalInterfaces.find((i: any) => i.id === formData.logicalInterface);

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      <button onClick={handleSubmit} className="btn-primary inline-flex items-center space-x-2">
        <Save className="w-4 h-4" />
        <span>{mode === 'add' ? 'Create Port Forward' : 'Save Changes'}</span>
      </button>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Port Forwarding Rule' : `Edit Port Forward: ${subscription?.subscription_name || subscription?.name}`}
      size="lg"
      footer={footer}
    >
      <div className="space-y-6">
        {/* Info Box */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start space-x-3">
          <ArrowRightLeft className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-purple-900">Port Forwarding (Destination NAT)</p>
            <p className="text-sm text-purple-700 mt-1">
              Forward incoming connections from a public port to an internal server.
            </p>
          </div>
        </div>

        {/* Form Fields */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="e.g., Web Server Forward"
              className={`input-base w-full ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="e.g., Forward HTTPS to internal web server"
              className="input-base w-full"
            />
          </div>

          {!lockedInterfaceId && !isDraft && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                WAN Interface <span className="text-red-500">*</span>
              </label>
              {loading ? (
                <p className="text-sm text-gray-500">Loading interfaces...</p>
              ) : (
                <select
                  value={formData.logicalInterface}
                  onChange={(e) => handleChange('logicalInterface', e.target.value)}
                  className={`input-base w-full ${errors.logicalInterface ? 'border-red-500' : ''}`}
                >
                  <option value="">Select WAN interface</option>
                  {wanInterfaces.map((iface: any) => (
                    <option key={iface.id} value={iface.id}>
                      {iface.name} ({iface.ipv4 || iface.ip || '-'})
                    </option>
                  ))}
                </select>
              )}
              {errors.logicalInterface && <p className="mt-1 text-xs text-red-600">{errors.logicalInterface}</p>}
            </div>
          )}

          {/* VSNP-691 + VSNP-713: External IP picker — only visible when the
              parent WAN has alias IPs. With no aliases the dropdown is
              redundant (single IP) and we let the backend default to the
              primary. Aliases may now be in the legacy string form or the
              new {cidr, gateway?} object form; we accept both here. */}
          {(() => {
            const parent = isDraft ? draftInterface : logicalInterfaces.find((i: any) => i.id === formData.logicalInterface);
            const aliasesRaw: any[] = Array.isArray(parent?.ipv4Aliases) ? parent.ipv4Aliases : [];
            if (aliasesRaw.length === 0) return null;

            const primaryDisplay = parent?.ipv4 ? String(parent.ipv4).split('/')[0] : '';
            const aliasIPs = aliasesRaw
              .map((a) => {
                const cidr = typeof a === 'string' ? a : (a?.cidr || '');
                return typeof cidr === 'string' ? cidr.split('/')[0] : '';
              })
              .filter(Boolean);

            return (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  External IP
                </label>
                <select
                  value={formData.externalIp}
                  onChange={(e) => handleChange('externalIp', e.target.value)}
                  className="input-base w-full"
                >
                  <option value="">{primaryDisplay ? `Primary — ${primaryDisplay}` : 'Primary'}</option>
                  {aliasIPs.map((ip) => (
                    <option key={ip} value={ip}>{ip} (alias)</option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Pick which of this WAN's IPs the inbound port forwards from.
                  Two forwards on the same external port can target different
                  internal services as long as they pin to different IPs.
                </p>
              </div>
            );
          })()}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Protocol</label>
              <select
                value={formData.protocol}
                onChange={(e) => handleChange('protocol', e.target.value)}
                className="input-base w-full"
              >
                <option value="TCP">TCP</option>
                <option value="UDP">UDP</option>
                <option value="Both">Both (TCP & UDP)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                External Port <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.externalPort}
                onChange={(e) => handleChange('externalPort', e.target.value)}
                placeholder="e.g., 443 or 8080"
                className={`input-base w-full ${errors.externalPort ? 'border-red-500' : ''}`}
              />
              {errors.externalPort && <p className="mt-1 text-xs text-red-600">{errors.externalPort}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal IP Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.internalIp}
                onChange={(e) => handleChange('internalIp', e.target.value)}
                placeholder="e.g., 192.168.1.50"
                className={`input-base w-full ${errors.internalIp ? 'border-red-500' : ''}`}
              />
              {errors.internalIp && <p className="mt-1 text-xs text-red-600">{errors.internalIp}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Internal Port <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.internalPort}
                onChange={(e) => handleChange('internalPort', e.target.value)}
                placeholder="e.g., 443 or 80"
                className={`input-base w-full ${errors.internalPort ? 'border-red-500' : ''}`}
              />
              {errors.internalPort && <p className="mt-1 text-xs text-red-600">{errors.internalPort}</p>}
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="pf-enabled"
              checked={formData.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded"
            />
            <label htmlFor="pf-enabled" className="text-sm font-medium text-gray-700">
              Enabled
            </label>
          </div>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-700 mb-2">Preview:</p>
          <p className="text-xs text-gray-600 font-mono">
            External: {selectedInterface?.name || 'WAN'}:{formData.externalPort || '443'} →
            Internal: {formData.internalIp || '192.168.1.50'}:{formData.internalPort || '443'}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default PortForwardingModal;
